import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1736863990093-a20c1c4a39ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920&auto=format&fit=crop";
const LINES = ["ARTE NA PELE.", "IDENTIDADE", "ETERNA."];

const MaskedLine = ({ children, delay, accent }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-7xl ${accent ? "text-[#e03131]" : "text-[#e2d9cc]"}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ sub }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="inicio" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0 -z-10" style={{ y: bgY }}>
        <img src={HERO_BG} alt="Estúdio Dudu Tattoo" className="w-full h-[120%] object-cover object-center opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/70 via-[#09090b]/40 to-[#09090b]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#09090b_85%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto w-full px-5 sm:px-8 pt-36 pb-10">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }}
          className="font-mono-dt text-xs uppercase tracking-[0.35em] text-[#e03131] mb-6" data-testid="hero-eyebrow">
          Estúdio Autoral de Tatuagem
        </motion.p>
        <h1 data-testid="hero-headline">
          {LINES.map((line, i) => (
            <MaskedLine key={line} delay={0.25 + i * 0.14} accent={i === 2}>{line}</MaskedLine>
          ))}
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-7 max-w-xl text-sm sm:text-base text-zinc-400 leading-relaxed" data-testid="hero-subheadline">
          {sub || "Estúdio autoral especializado em Blackwork, Realismo Black & Grey e Projetos Exclusivos."}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4">
          <a href="#agendamento" data-testid="hero-agendar-cta"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-[#e03131] text-white font-mono-dt text-xs uppercase tracking-[0.2em] rounded-md hover:bg-[#c92a2a] transition-colors duration-300">
            Agendar Tatuagem
            <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
          <a href="#galeria" data-testid="hero-galeria-cta"
            className="inline-flex items-center px-7 py-4 border border-[#27272a] text-[#e2d9cc] font-mono-dt text-xs uppercase tracking-[0.2em] rounded-md hover:border-[#e03131] hover:text-[#e03131] transition-colors duration-300">
            Explorar Galeria
          </a>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.25, duration: 0.8 }}
        className="relative border-t border-[#27272a]/60 bg-[#09090b]/60 backdrop-blur-md" data-testid="hero-stats-bar">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#27272a]/60">
          {[
            ["4+", "Anos de História"],
            ["5.000+", "Tatuagens Realizadas"],
            ["100%", "Biossegurança"],
          ].map(([n, label]) => (
            <div key={label} className="py-5 sm:py-6 sm:px-8 first:pl-0 flex items-center gap-4">
              <span className="font-display font-black text-2xl sm:text-3xl text-[#e2d9cc]">{n}</span>
              <span className="font-mono-dt text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-2">
                <ShieldCheck size={13} className="text-[#e03131]" />{label}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
